import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSubscription } from "../contexts/SubscriptionContext";
import { verifyCheckoutSession } from "../api/subscriptionClient";

const MAX_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

export default function SubscriptionSuccess() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { refreshSubscription } = useSubscription();
    const [status, setStatus] = useState("loading"); // loading, success, timeout
    const hasStarted = useRef(false);

    useEffect(() => {
        if (hasStarted.current) return;
        hasStarted.current = true;

        const sessionId = searchParams.get("session_id");

        const poll = async () => {
            for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
                try {
                    // If we have a session ID, verify directly with the backend
                    if (sessionId) {
                        const result = await verifyCheckoutSession(sessionId);
                        if (result.isActive) {
                            await refreshSubscription();
                            setStatus("success");
                            return;
                        }
                    } else {
                        // No session ID, just check subscription status
                        await refreshSubscription();
                        // refreshSubscription updates context; check via a fresh status call
                        const { getSubscriptionStatus } = await import("../api/subscriptionClient");
                        const result = await getSubscriptionStatus();
                        if (result.isActive) {
                            setStatus("success");
                            return;
                        }
                    }
                } catch (error) {
                    // Ignore errors during polling, keep trying
                }

                // Wait before next attempt
                await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
            }

            // Exhausted all attempts
            setStatus("timeout");
        };

        poll();
    }, [searchParams, refreshSubscription]);

    const handleContinue = () => {
        navigate("/portfolio");
    };

    if (status === "loading") {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full card p-8 text-center">
                    <div className="w-16 h-16 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-accent-600 animate-spin" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">
                        Setting up your subscription...
                    </h1>
                    <p className="text-gray-500">
                        Please wait while we confirm your payment.
                    </p>
                </div>
            </div>
        );
    }

    if (status === "timeout") {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full card p-8 text-center">
                    <div className="w-16 h-16 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">
                        Your payment was received!
                    </h1>
                    <p className="text-gray-500 mb-6">
                        It may take a moment to activate. Please refresh the page or click below to continue.
                    </p>
                    <button
                        onClick={handleContinue}
                        className="w-full btn-primary py-3 text-lg font-semibold"
                    >
                        Continue to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full card p-8 text-center">
                <div className="w-16 h-16 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                    </svg>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Welcome to OmniWorth!
                </h1>
                <p className="text-gray-500 mb-8">
                    Your subscription is now active. You have full access to all features.
                </p>

                <button
                    onClick={handleContinue}
                    className="w-full btn-primary py-3 text-lg font-semibold"
                >
                    Get Started
                </button>
            </div>
        </div>
    );
}
